import { CSSProperties, ReactNode, useMemo } from "react";
import { Link, useLoaderData, useNavigate, useSearchParams } from "react-router-dom";

import { laborCategorys, laborShopsCategorys } from "src/components/works/types";
import {
    boolAndReducer,
    getDatefromWeekNum,
    MyDate,
    round,
    Select,
    simpleStatusColorCode,
    weeknum,
} from "src/components/common/functions";

type CalendarAssignment = {
    assignmentId: string;
    workId: string;
    projectNumber: string;
    organizationName: string;
    clientProjectNumber: string;
    laborCategory: string;
    category: string;
    userName?: string;
    truckNumber?: string;
    assignmentStatus: string;
    billed?: string;
    startTime: string | null;
    endTime: string | null;
    travelStartTime: string | null;
    workStartTime: string | null;
    workEndTime: string | null;
    travelEndTime: string | null;
};

type LoaderData = {
    weeks: string[];
    selectedWeek: string;
    activeDate: string;
    view: CalendarView;
    rangeStart: string;
    rangeEnd: string;
    assignments: CalendarAssignment[];
};

type CalendarView = "month" | "week" | "day";

type CalendarAssignmentView = Omit<
    CalendarAssignment,
    "startTime" | "endTime" | "travelStartTime" | "workStartTime" | "workEndTime" | "travelEndTime"
> & {
    startTime: string;
    endTime: string;
    travelStartTime: string;
    workStartTime: string;
    workEndTime: string;
    travelEndTime: string;
    portal: number | string;
    withoutTravel: number | string;
    projectName: string;
    userName: string;
    startDate: Date;
    endDate: Date;
    startDateKey: string;
    endDateKey: string;
    displayStart: string;
    displayEnd: string;
};

type TimedSegment = {
    assignment: CalendarAssignmentView;
    dayKey: string;
    top: number;
    height: number;
    startsBefore: boolean;
    endsAfter: boolean;
};

type UserOption = {
    label: string;
    value: string;
};

const title = "Calendar";
const calendarCategories = [
    "Restoration",
    "Emergency",
    "Asphalt",
    "Concrete",
    "Sewer",
    "Water",
    "Potholing",
    "Locating",
    "CCTV",
    "Mapping",
    "Surveying",
    "Traffic",
    "Hydro",
    "Straight",
    "Shop",
];
const dayNamesShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const dayNamesLong = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const statusLegend = [
    { key: "created", label: "Unsubmitted", className: "calendarLegendChipUnsubmitted" },
    { key: "submitted", label: "Submitted", className: "calendarLegendChipSubmitted" },
    { key: "approved", label: "Approved", className: "calendarLegendChipApproved" },
    { key: "billed", label: "Billed", className: "calendarLegendChipBilled" },
    { key: "rejected", label: "Rejected", className: "calendarLegendChipRejected" },
] as const;
const hourLabels = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: formatHourLabel(hour),
}));
const hourHeight = 64;
const minimumEventHeight = 28;

export default function Calendar() {
    const navigate = useNavigate();
    const { weeks, selectedWeek, activeDate, view, assignments } = useLoaderData() as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get("status") ?? "All";
    const showTimes = searchParams.get("showTimes") === "1";
    const showHours = searchParams.get("showHours") === "1";
    const category = searchParams.get("category") ?? "All";
    const laborCategory = searchParams.get("laborCategory") ?? "All";
    const filterWords = searchParams.get("q") ?? "";
    const selectedUsers = useMemo(
        () => (searchParams.get("users") ?? "").split(",").map(value => value.trim()).filter(Boolean),
        [searchParams]
    );

    const getHours = (assignment: CalendarAssignment) => {
        if (assignment.assignmentStatus === "Created") {
            return { portal: 0, withoutTravel: 0 };
        }
        const travelStartTime = new Date(normalizeDateTime(assignment.travelStartTime));
        const workStartTime = new Date(normalizeDateTime(assignment.workStartTime));
        const workEndTime = new Date(normalizeDateTime(assignment.workEndTime));
        const travelEndTime = new Date(normalizeDateTime(assignment.travelEndTime));
        const workHours = (workEndTime.getTime() - workStartTime.getTime()) / 3600000;
        const travel = (travelEndTime.getTime() - travelStartTime.getTime()) / 3600000;
        if (
            Number.isNaN(travelStartTime.getTime()) ||
            Number.isNaN(workStartTime.getTime()) ||
            Number.isNaN(workEndTime.getTime()) ||
            Number.isNaN(travelEndTime.getTime())
        ) {
            return { portal: "Invalid Date", withoutTravel: "Invalid Date" };
        }
        return {
            portal: round(travel, 2),
            withoutTravel: round(workHours, 2),
        };
    };

    const extendedAssignments = useMemo<CalendarAssignmentView[]>(() => assignments.map(rawAssignment => {
        const assignment = {
            ...rawAssignment,
            travelStartTime: normalizeDateTime(rawAssignment.travelStartTime),
            workStartTime: normalizeDateTime(rawAssignment.workStartTime),
            workEndTime: normalizeDateTime(rawAssignment.workEndTime),
            travelEndTime: normalizeDateTime(rawAssignment.travelEndTime),
            startTime: normalizeDateTime(rawAssignment.startTime),
            endTime: normalizeDateTime(rawAssignment.endTime),
        };
        const startDate = new Date(assignment.startTime);
        const parsedEndDate = new Date(assignment.endTime);
        const endDate = parsedEndDate.getTime() > startDate.getTime()
            ? parsedEndDate
            : new Date(startDate.getTime() + 60 * 60 * 1000);
        return {
            ...assignment,
            ...getHours(assignment),
            projectName: `${assignment.projectNumber} - ${assignment.organizationName} - ${assignment.clientProjectNumber}`,
            userName: assignment.userName?.replace(/ {2}/g, " ") ?? "unknown",
            startDate,
            endDate,
            startDateKey: assignment.startTime.substring(0, 10).replace(/\//g, "-"),
            endDateKey: assignment.endTime.substring(0, 10).replace(/\//g, "-"),
            displayStart: formatClockTime(startDate),
            displayEnd: formatClockTime(endDate),
        };
    }), [assignments]);

    const filteredAssignments = useMemo(() => extendedAssignments.filter(assignment =>
        ((status !== "All")
            ? (
                status === "Unsubmitted"
                    ? assignment.assignmentStatus === "Created"
                    : status === "Billed"
                        ? assignment.billed === "Yes"
                        : assignment.assignmentStatus === status
            )
            : true) &&
        assignment.laborCategory !== "Images" &&
        (selectedUsers.length ? selectedUsers.includes(assignment.userName) : true) &&
        (laborCategory !== "All" ? assignment.laborCategory === laborCategory : true) &&
        (category !== "All" ? assignment.category === category : true) &&
        filterWords.toLowerCase().split(" ").map(word => JSON.stringify(
            [assignment.userName, assignment.projectName, assignment.truckNumber]
        ).toLowerCase().indexOf(word) > -1).reduce(boolAndReducer, true)
    ), [extendedAssignments, filterWords, laborCategory, selectedUsers, status, category]);

    const userOptions = useMemo<UserOption[]>(() => Array.from(
        new Set(extendedAssignments.map(assignment => assignment.userName).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b)).map(userName => ({
        label: userName,
        value: userName,
    })), [extendedAssignments]);

    const selectedUserOptions = useMemo(
        () => userOptions.filter(option => selectedUsers.includes(option.value)),
        [selectedUsers, userOptions]
    );

    const summary = {
        created: filteredAssignments.filter(value => value.assignmentStatus === "Created").length,
        submitted: filteredAssignments.filter(value => value.assignmentStatus === "Submitted").length,
        approved: filteredAssignments.filter(value => value.assignmentStatus === "Approved").length,
        billed: filteredAssignments.filter(value => value.billed === "Yes").length,
        rejected: filteredAssignments.filter(value => value.assignmentStatus === "Rejected").length,
    };

    const activeDateObject = useMemo(() => new MyDate(activeDate.replace(/-/g, "/")), [activeDate]);
    const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => getDatefromWeekNum(selectedWeek, index) ?? ""), [selectedWeek]);
    const visibleDates = useMemo(() => (view === "day" ? [activeDate] : view === "week" ? weekDates : []), [activeDate, view, weekDates]);
    const monthGridDates = useMemo(() => buildMonthGrid(activeDateObject), [activeDateObject]);
    const allDaySegments = useMemo(() => {
        const result: Record<string, CalendarAssignmentView[]> = {};
        visibleDates.forEach(date => {
            result[date] = filteredAssignments.filter(assignment => isAllDayAssignment(assignment) && intersectsDate(assignment, date));
        });
        return result;
    }, [filteredAssignments, visibleDates]);

    const timedSegments = useMemo(() => {
        const result: Record<string, TimedSegment[]> = {};
        visibleDates.forEach(date => {
            result[date] = filteredAssignments
                .filter(assignment => !isAllDayAssignment(assignment))
                .map(assignment => getTimedSegment(assignment, date))
                .filter((segment): segment is TimedSegment => Boolean(segment))
                .sort((a, b) => a.top - b.top || b.height - a.height);
        });
        return result;
    }, [filteredAssignments, visibleDates]);

    const monthAssignmentsByDate = useMemo(() => {
        const result: Record<string, CalendarAssignmentView[]> = {};
        monthGridDates.forEach(date => {
            result[date] = filteredAssignments
                .filter(assignment => intersectsDate(assignment, date))
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        });
        return result;
    }, [filteredAssignments, monthGridDates]);

    const navigateToState = (nextDate: string, nextView: CalendarView = view) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("date", nextDate);
        nextParams.set("view", nextView);
        navigate(`/${title}/${weeknum(nextDate)}?${nextParams.toString()}`);
    };

    const navigateByOffset = (offset: number) => {
        const nextDate = shiftDate(activeDate, view === "month" ? { months: offset } : { days: view === "week" ? offset * 7 : offset });
        navigateToState(nextDate);
    };

    return (
        <div className = "calendarPage">
            <section className = "calendarToolbar">
                <div className = "calendarUserRow">
                    <Field label = "Users" htmlFor = "calendarUsers">
                        <Select
                            inputId = "calendarUsers"
                            classNamePrefix = "custom-select"
                            isMulti = {true}
                            isClearable = {true}
                            placeholder = "All users"
                            options = {userOptions}
                            value = {selectedUserOptions}
                            onChange = {(next: UserOption[] | null) => {
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.set("users", (next ?? []).map(option => option.value).join(","));
                                setSearchParams(nextParams);
                            }}
                        />
                    </Field>
                </div>

                <div className = "calendarToolbarGrid">
                    <Field label = "Search" htmlFor = "calendarSearch">
                        <input
                            id = "calendarSearch"
                            className = "form-control"
                            type = "text"
                            value = {filterWords}
                            onChange = {e => updateSearchParam(setSearchParams, searchParams, "q", e.currentTarget.value.toLowerCase())}
                        />
                    </Field>
                    <Field label = "Status" htmlFor = "calendarStatus">
                        <select
                            id = "calendarStatus"
                            className = "form-control"
                            value = {status}
                            onChange = {e => updateSearchParam(setSearchParams, searchParams, "status", e.currentTarget.value)}
                        >
                            <option>All</option>
                            <option>Unsubmitted</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Rejected</option>
                            <option>Billed</option>
                        </select>
                    </Field>
                    <Field label = "Calendar" htmlFor = "calendarCategory">
                        <select
                            id = "calendarCategory"
                            className = "form-control"
                            value = {category}
                            onChange = {e => {
                                const next = new URLSearchParams(searchParams);
                                next.set("category", e.currentTarget.value);
                                next.set("laborCategory", "All");
                                setSearchParams(next);
                            }}
                        >
                            <option>All</option>
                            {calendarCategories.map((option, index) => (
                                <option key = {index}>{option}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label = "Labor Category" htmlFor = "calendarLaborCategory">
                        <select
                            id = "calendarLaborCategory"
                            className = "form-control"
                            value = {laborCategory}
                            onChange = {e => updateSearchParam(setSearchParams, searchParams, "laborCategory", e.currentTarget.value)}
                        >
                            <option>All</option>
                            <optgroup label = "Billable Work">
                                {laborCategorys.map((value, index) => <option key = {index}>{value}</option>)}
                            </optgroup>
                            <optgroup label = "Shop Work">
                                {laborShopsCategorys.map((value, index) => <option key = {index}>{value}</option>)}
                            </optgroup>
                            <optgroup label = "Others">
                                <option>Images</option>
                            </optgroup>
                        </select>
                    </Field>
                </div>
                <div className = "calendarToolbarFooter">
                    <ToggleField label = "Show Times" htmlFor = "calendarShowTimes">
                        <input
                            id = "calendarShowTimes"
                            type = "checkbox"
                            className = "cssCheckBox"
                            checked = {showTimes}
                            onChange = {e => updateSearchParam(setSearchParams, searchParams, "showTimes", e.currentTarget.checked ? "1" : "0")}
                        />
                    </ToggleField>
                    <ToggleField label = "Show Hours" htmlFor = "calendarShowHours">
                        <input
                            id = "calendarShowHours"
                            type = "checkbox"
                            className = "cssCheckBox"
                            checked = {showHours}
                            onChange = {e => updateSearchParam(setSearchParams, searchParams, "showHours", e.currentTarget.checked ? "1" : "0")}
                        />
                    </ToggleField>
                </div>
            </section>
            <section className = "calendarLegend">
                {statusLegend.map(item => (
                    <div className = {`calendarLegendChip ${item.className}`} key = {item.key}>
                        <span>{item.label}</span>
                        <strong>{summary[item.key]}</strong>
                    </div>
                ))}
            </section>
            <div className = "calendarNavInputs">
                <div className = "calendarViewSwitch" role = "tablist" aria-label = "Calendar views">
                    {(["month", "week", "day"] as CalendarView[]).map(option => (
                        <button
                            key = {option}
                            className = {`calendarViewButton${view === option ? " active" : ""}`}
                            type = "button"
                            onClick = {() => navigateToState(activeDate, option)}
                        >
                            {capitalize(option)}
                        </button>
                    ))}
                </div>
                {view === "month" ? (
                    <Field htmlFor = "calendarMonth">
                        <select
                            id = "calendarMonth"
                            className = "form-control"
                            value = {activeDate.slice(0, 7)}
                            onChange = {e => {
                                const nextDate = `${e.currentTarget.value}-01`;
                                navigateToState(nextDate);
                            }}
                        >
                            {getMonthOptions(weeks).map((value, index) => (
                                <option key = {index} value = {value}>
                                    {formatMonthOption(value)}
                                </option>
                            ))}
                        </select>
                    </Field>
                ) : null}
                {view === "week" ? (
                    <Field htmlFor = "calendarWeek">
                        <select
                            id = "calendarWeek"
                            className = "form-control"
                            value = {selectedWeek}
                            onChange = {e => {
                                const nextDate = getDatefromWeekNum(e.currentTarget.value, 0) ?? activeDate;
                                navigateToState(nextDate);
                            }}
                        >
                            {weeks.map((value, index) => <option key = {index}>{value}</option>)}
                        </select>
                    </Field>
                ) : null}
                {view === "day" ? (
                    <Field htmlFor = "calendarDate">
                        <input
                            id = "calendarDate"
                            className = "form-control"
                            type = "date"
                            value = {activeDate}
                            onChange = {e => navigateToState(e.currentTarget.value)}
                        />
                    </Field>
                ) : null}
                <div className = "calendarNavButtons">
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(-1)}>Prev</button>
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(1)}>Next</button>
                </div>
            </div>
            {view === "month" ? (
                <MonthView
                    activeDate = {activeDate}
                    monthGridDates = {monthGridDates}
                    assignmentsByDate = {monthAssignmentsByDate}
                    onSelectDate = {date => navigateToState(date, "day")}
                    showTimes = {showTimes}
                    showHours = {showHours}
                />
            ) : (
                <TimedView
                    view = {view}
                    visibleDates = {visibleDates}
                    allDaySegments = {allDaySegments}
                    timedSegments = {timedSegments}
                    showTimes = {showTimes}
                    showHours = {showHours}
                    onSelectDate = {date => navigateToState(date, "day")}
                />
            )}
        </div>
    );
}

function TimedView({
    view,
    visibleDates,
    allDaySegments,
    timedSegments,
    showTimes,
    showHours,
    onSelectDate,
}: {
    view: CalendarView;
    visibleDates: string[];
    allDaySegments: Record<string, CalendarAssignmentView[]>;
    timedSegments: Record<string, TimedSegment[]>;
    showTimes: boolean;
    showHours: boolean;
    onSelectDate: (date: string) => void;
}) {
    const gridHeight = hourHeight * 24;
    const hasTimedAssignments = visibleDates.some(date => timedSegments[date]?.length || allDaySegments[date]?.length);

    return (
        <section className = "calendarTimedShell">
            <div className = "calendarAllDayRow">
                <div className = "calendarTimeAxisSpacer">All day</div>
                <div className = {`calendarAllDayColumns cols-${visibleDates.length}`}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarAllDayColumn" key = {date}>
                            <button className = "calendarTimedDayHeader" type = "button" onClick = {() => onSelectDate(date)}>
                                <span>{view === "week" ? dayNamesShort[index] : dayNamesLong[(new Date(date.replace(/-/g, "/"))).getDay() === 0 ? 6 : (new Date(date.replace(/-/g, "/"))).getDay() - 1]}</span>
                                <strong>{formatShortDate(date)}</strong>
                            </button>
                            <div className = "calendarAllDayEvents">
                                {allDaySegments[date]?.length ? allDaySegments[date].map(item => (
                                    <AssignmentPill key = {`${date}-${item.assignmentId}`} assignment = {item} showHours = {showHours} />
                                )) : <div className = "calendarNoAllDay">No all-day events</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className = "calendarTimedFrame">
                <div className = "calendarTimeAxis">
                    {hourLabels.map(row => (
                        <div className = "calendarTimeAxisRow" key = {row.hour} style = {{ height: hourHeight }}>
                            <span>{row.label}</span>
                        </div>
                    ))}
                </div>
                <div className = {`calendarTimedColumns cols-${visibleDates.length}`} style = {{ minHeight: gridHeight }}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarTimedColumn" key = {date}>
                            <div className = "calendarTimedColumnGrid">
                                {hourLabels.map(row => (
                                    <div className = "calendarTimedHourSlot" key = {row.hour} style = {{ height: hourHeight }} />
                                ))}
                            </div>
                            <div className = "calendarTimedEventsLayer">
                                {timedSegments[date]?.map(segment => (
                                    <TimedEventCard
                                        key = {`${date}-${segment.assignment.assignmentId}-${segment.top}`}
                                        segment = {segment}
                                        showTimes = {showTimes}
                                        showHours = {showHours}
                                    />
                                ))}
                                {!timedSegments[date]?.length && !allDaySegments[date]?.length ? (
                                    <div className = "calendarTimedEmpty">No assignments</div>
                                ) : null}
                            </div>
                            {view === "week" && index < visibleDates.length - 1 ? <div className = "calendarTimedColumnDivider" /> : null}
                        </div>
                    ))}
                    {!hasTimedAssignments ? <div className = "calendarTimedNoResults">No assignments match the current filters.</div> : null}
                </div>
            </div>
        </section>
    );
}

function MonthView({
    activeDate,
    monthGridDates,
    assignmentsByDate,
    onSelectDate,
    showTimes,
    showHours,
}: {
    activeDate: string;
    monthGridDates: string[];
    assignmentsByDate: Record<string, CalendarAssignmentView[]>;
    onSelectDate: (date: string) => void;
    showTimes: boolean;
    showHours: boolean;
}) {
    const activeMonth = activeDate.slice(0, 7);

    return (
        <section className = "calendarMonthView">
            <div className = "calendarMonthHeader">
                {dayNamesShort.map(day => <div className = "calendarMonthHeaderCell" key = {day}>{day}</div>)}
            </div>
            <div className = "calendarMonthGrid">
                {monthGridDates.map(date => {
                    const items = assignmentsByDate[date] ?? [];
                    const inMonth = date.slice(0, 7) === activeMonth;
                    return (
                        <div
                            className = {`calendarMonthCell${inMonth ? "" : " outside"}${date === activeDate ? " active" : ""}`}
                            key = {date}
                        >
                            <button
                                className = "calendarMonthDateButton"
                                type = "button"
                                onClick = {() => onSelectDate(date)}
                            >
                                <div className = "calendarMonthDateRow">
                                    <span>{date.slice(8, 10)}</span>
                                    <strong>{items.length || ""}</strong>
                                </div>
                            </button>
                            <div className = "calendarMonthAssignments">
                                {items.slice(0, 3).map(item => (
                                    <Link className = "calendarMonthAssignment calendarMonthAssignmentLink" to = {`/Assignments/${item.assignmentId}`} key = {`${date}-${item.assignmentId}`}>
                                        <span className = "calendarMonthAssignmentDot" style = {{ backgroundColor: getStatusColor(item.assignmentStatus) }} />
                                        <span className = "calendarMonthAssignmentText">
                                            {showTimes && !isAllDayAssignment(item) ? `${item.displayStart} ` : ""}
                                            {item.userName}
                                        </span>
                                        {showHours && item.assignmentStatus !== "Created" ? (
                                            <span className = "calendarMonthAssignmentHours">{String(item.withoutTravel)}h</span>
                                        ) : null}
                                    </Link>
                                ))}
                                {items.length > 3 ? <div className = "calendarMonthMore">+{items.length - 3} more</div> : null}
                                {!items.length ? <div className = "calendarMonthEmpty">No assignments</div> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function AssignmentPill({ assignment, showHours }: { assignment: CalendarAssignmentView; showHours: boolean }) {
    return (
        <Link className = "calendarAllDayEvent" to = {`/Assignments/${assignment.assignmentId}`}>
            <span className = "calendarStatusDot" style = {{ backgroundColor: getStatusColor(assignment.assignmentStatus) }} />
            <span className = "calendarAllDayText">{assignment.userName}</span>
            {showHours && assignment.assignmentStatus !== "Created" ? (
                <span className = "calendarAllDayHours">{String(assignment.withoutTravel)}h</span>
            ) : null}
        </Link>
    );
}

function TimedEventCard({
    segment,
    showTimes,
    showHours,
}: {
    segment: TimedSegment;
    showTimes: boolean;
    showHours: boolean;
}) {
    const { assignment } = segment;
    const style: CSSProperties = {
        top: segment.top,
        height: segment.height,
        borderLeftColor: getStatusColor(assignment.assignmentStatus),
    };

    return (
        <Link className = "calendarTimedEvent" style = {style} to = {`/Assignments/${assignment.assignmentId}`}>
            <div className = "calendarTimedEventTitle">{assignment.userName}</div>
            <div className = "calendarTimedEventProject">{assignment.projectNumber}</div>
            <div className = "calendarTimedEventMeta">
                {showTimes || true ? `${segment.startsBefore ? "..." : assignment.displayStart} - ${segment.endsAfter ? "..." : assignment.displayEnd}` : ""}
            </div>
            <div className = "calendarTimedEventMeta">{assignment.laborCategory}</div>
            {assignment.truckNumber ? <div className = "calendarTimedEventMeta">{assignment.truckNumber}</div> : null}
            {showHours && assignment.assignmentStatus !== "Created" ? (
                <div className = "calendarTimedEventMeta">{String(assignment.withoutTravel)}h work</div>
            ) : null}
        </Link>
    );
}

function Field({ label, htmlFor, children }: { label?: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarField" htmlFor = {htmlFor}>
            {label?<span className = "calendarFieldLabel">{label}</span>:null}
            {children}
        </label>
    );
}

function ToggleField({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarToggle" htmlFor = {htmlFor}>
            <span className = "calendarToggleText">{label}</span>
            {children}
        </label>
    );
}

function updateSearchParam(
    setSearchParams: ReturnType<typeof useSearchParams>[1],
    searchParams: URLSearchParams,
    key: string,
    value: string
) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next);
}

function normalizeDateTime(value: string | null | undefined) {
    return (value ?? "").replace(/-/g, "/");
}

function isAllDayAssignment(item: CalendarAssignmentView) {
    return item.startTime.substring(11, 16) === "00:00" && item.endTime.substring(11, 16) === "00:00";
}

function intersectsDate(item: CalendarAssignmentView, date: string) {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    return item.endDate > dayStart && item.startDate < dayEnd;
}

function getTimedSegment(item: CalendarAssignmentView, date: string): TimedSegment | null {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    if (item.endDate <= dayStart || item.startDate >= dayEnd) return null;

    const segmentStart = item.startDate < dayStart ? dayStart : item.startDate;
    const segmentEnd = item.endDate > dayEnd ? dayEnd : item.endDate;
    const top = (((segmentStart.getHours() * 60) + segmentStart.getMinutes()) / 60) * hourHeight;
    const rawHeight = ((segmentEnd.getTime() - segmentStart.getTime()) / 3600000) * hourHeight;

    return {
        assignment: item,
        dayKey: date,
        top,
        height: Math.max(minimumEventHeight, rawHeight),
        startsBefore: item.startDate < dayStart,
        endsAfter: item.endDate > dayEnd,
    };
}

function buildMonthGrid(activeDate: Date) {
    const firstOfMonth = new MyDate(activeDate.getFullYear(), activeDate.getMonth(), 1);
    const firstDayIndex = getMondayOffset(firstOfMonth.s());
    const gridStart = new MyDate(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstDayIndex);

    return Array.from({ length: 42 }, (_, index) => {
        const date = new MyDate(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date.s();
    });
}

function getMonthOptions(weeks: string[]) {
    return Array.from(new Set(weeks
        .map(week => getDatefromWeekNum(week, 0)?.slice(0, 7))
        .filter((value): value is string => Boolean(value))
    ));
}

function formatMonthOption(value: string) {
    return new Date(`${value}-01`.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
}

function getMondayOffset(dateString: string) {
    const weekday = new Date(dateString.replace(/-/g, "/")).getDay();
    return weekday === 0 ? 6 : weekday - 1;
}

function shiftDate(dateString: string, offset: { days?: number; months?: number }) {
    const date = new MyDate(dateString.replace(/-/g, "/"));
    if (offset.months) {
        date.setMonth(date.getMonth() + offset.months);
    }
    if (offset.days) {
        date.setDate(date.getDate() + offset.days);
    }
    return date.s();
}

function formatHourLabel(hour: number) {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
}

function formatClockTime(date: Date) {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";
    const normalizedHour = hours % 12 || 12;
    return `${normalizedHour}:${minutes} ${suffix}`;
}

function formatShortDate(date: string) {
    return new Date(date.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function getStatusColor(status: string) {
    return simpleStatusColorCode[status as keyof typeof simpleStatusColorCode] ?? "black";
}
